import { addItem, emptyInventory, removeItem, type Inventory, type InventoryItem } from "../inventory/Inventory";
import { KnowledgeGraph, type KnowledgeCatalog, type KnowledgeProgress } from "../knowledge/KnowledgeGraph";
import type { QuestProgress } from "../quests/QuestDefinition";
import { CURRENT_SAVE_VERSION, type GameSave, type Preferences, type WorldSnapshot } from "../persistence/GameSave";
import { defaultPreferences } from "../persistence/GameSave";
import { addEntity, findEntity, instantiateScene, removeEntity, replaceEntity, type Scene } from "./Scene";
import type { SceneDefinition, EntityDefinition } from "./SceneDefinition";
import { withTransform, type Entity, type EntityId } from "./Entity";
import type { Transform } from "./geometry";
import type { EntityDefinitionEntry, EntityDefinitionRegistry } from "../game-content/EntityDefinitionRegistry";
import { computeSceneDiff, applySceneDiff, type SceneDiff } from "./scene-diff";
import type { ParsedTerrainObject } from "./terrain";
import type { EntityComponent, ComponentType } from "./components";

export interface WorldOptions {
  readonly knowledgeCatalog: KnowledgeCatalog;
  readonly entityDefinitions: EntityDefinitionRegistry;
  readonly snapshot?: WorldSnapshot;
  readonly knowledgeProgress?: KnowledgeProgress;
  readonly inventory?: Inventory;
  readonly preferences?: Preferences;
}

const EMPTY_DIFF: SceneDiff = { modified: {}, removed: [], added: [] };

/**
 * Root of the serializable game state: visited scenes, player inventory, knowledge
 * progress, quest/secret progress and preferences. Holds no PixiJS references.
 *
 * Persistence model: entities live on the map (scene definition JSON) by default.
 * Only diffs from the original definitions (moves, takes, transforms, etc.) are
 * saved. On load, scenes are instantiated from definitions then diffs applied.
 */
export class World {
  readonly knowledge: KnowledgeGraph;
  inventory: Inventory;
  preferences: Preferences;

  private readonly entityDefinitions: EntityDefinitionRegistry;
  private scenes = new Map<string, Scene>();
  private pendingDiffs: Map<string, SceneDiff>;
  private revealedSecrets: Set<string>;
  private questProgress: Map<string, QuestProgress>;
  private currentSceneId: string;

  constructor(options: WorldOptions) {
    this.entityDefinitions = options.entityDefinitions;
    this.knowledge = new KnowledgeGraph(options.knowledgeCatalog, options.knowledgeProgress ?? {});
    this.inventory = options.inventory ?? emptyInventory();
    this.preferences = options.preferences ?? defaultPreferences();
    this.currentSceneId = options.snapshot?.currentSceneId ?? "";
    this.pendingDiffs = new Map(Object.entries(options.snapshot?.sceneDiffs ?? {}));
    this.revealedSecrets = new Set(options.snapshot?.revealedSecrets ?? []);
    this.questProgress = new Map(Object.entries(options.snapshot?.questProgress ?? {}));
  }

  loadScene(definition: SceneDefinition, terrainObjects?: readonly ParsedTerrainObject[]): Scene {
    const mergedDefinition = terrainObjects && terrainObjects.length > 0
      ? mergeTerrainObjects(definition, terrainObjects)
      : definition;
    const existing = this.scenes.get(mergedDefinition.id);
    if (existing) {
      this.currentSceneId = mergedDefinition.id;
      return existing;
    }
    const baseEntities = instantiateScene(mergedDefinition, this.entityDefinitions).entities;
    const diff = this.pendingDiffs.get(mergedDefinition.id) ?? EMPTY_DIFF;
    const entities = applySceneDiff(baseEntities, diff);
    const scene: Scene = { id: mergedDefinition.id, definition: mergedDefinition, entities, initialEntities: baseEntities };
    this.scenes.set(mergedDefinition.id, scene);
    this.currentSceneId = mergedDefinition.id;
    return scene;
  }

  getCurrentSceneId(): string {
    return this.currentSceneId;
  }

  getCurrentScene(): Scene {
    const scene = this.scenes.get(this.currentSceneId);
    if (!scene) throw new Error(`No scene loaded for id "${this.currentSceneId}"`);
    return scene;
  }

  getScene(id: string): Scene | undefined {
    return this.scenes.get(id);
  }

  getEntity(id: EntityId): Entity | undefined {
    return findEntity(this.getCurrentScene(), id);
  }

  getEntityDefinition(definitionId: string): EntityDefinitionEntry | undefined {
    return this.entityDefinitions.get(definitionId);
  }

  updateEntity(id: EntityId, update: (entity: Entity) => Entity): Entity {
    const scene = this.getCurrentScene();
    const entity = findEntity(scene, id);
    if (!entity) throw new Error(`Unknown entity "${id}" in scene "${scene.id}"`);
    const updated = update(entity);
    this.scenes.set(scene.id, replaceEntity(scene, updated));
    return updated;
  }

  moveEntity(id: EntityId, transform: Partial<Transform>): Entity {
    return this.updateEntity(id, (e) => withTransform(e, transform));
  }

  removeEntityFromScene(id: EntityId): void {
    const scene = this.getCurrentScene();
    this.scenes.set(scene.id, removeEntity(scene, id));
  }

  addEntityToScene(entity: Entity): void {
    const scene = this.getCurrentScene();
    this.scenes.set(scene.id, addEntity(scene, entity));
  }

  revealEntity(id: EntityId): void {
    this.updateEntity(id, (e) => ({ ...e, visible: true }));
  }

  addToInventory(item: InventoryItem): void {
    this.inventory = addItem(this.inventory, item);
  }

  removeFromInventory(instanceId: string, quantity = 1): void {
    this.inventory = removeItem(this.inventory, instanceId, quantity);
  }

  updateInventoryItem(instanceId: string, update: (item: InventoryItem) => InventoryItem): void {
    const item = this.inventory.items.find((i) => i.instanceId === instanceId);
    if (!item) throw new Error(`Unknown inventory item "${instanceId}"`);
    const updated = update(item);
    this.inventory = {
      ...this.inventory,
      items: this.inventory.items.map((i) => (i.instanceId === instanceId ? updated : i)),
    };
  }

  isSecretRevealed(id: string): boolean {
    return this.revealedSecrets.has(id);
  }

  markSecretRevealed(id: string): void {
    this.revealedSecrets.add(id);
  }

  getQuestProgress(id: string): QuestProgress | undefined {
    return this.questProgress.get(id);
  }

  setQuestProgress(id: string, progress: QuestProgress): void {
    this.questProgress.set(id, progress);
  }

  /** Replaces all state in place from a loaded save. Existing references to this `World`
   *  instance (held by `CommandBus`, `RuleEngine`, ...) stay valid. */
  restore(save: GameSave): void {
    // Migrate legacy v2 saves (full entity snapshots) to v3 diff format.
    // Lost — can't reconstruct diffs without scene definitions at this point,
    // so affected scenes will re-instantiate from their JSON definitions.
    if (save.version < CURRENT_SAVE_VERSION && save.world.scenes) {
      const { scenes: _legacy, ...worldRest } = save.world;
      const migrated: GameSave = {
        ...save,
        version: CURRENT_SAVE_VERSION,
        world: { ...worldRest, sceneDiffs: {} },
      };
      this.restoreV3(migrated);
      return;
    }
    this.restoreV3(save);
  }

  private restoreV3(save: GameSave): void {
    this.inventory = save.player.inventory;
    this.knowledge.loadProgress(save.player.knowledge);
    this.currentSceneId = save.world.currentSceneId;
    this.pendingDiffs = new Map(Object.entries(save.world.sceneDiffs));
    this.scenes.clear();
    this.revealedSecrets = new Set(save.world.revealedSecrets);
    this.questProgress = new Map(Object.entries(save.world.questProgress));
  }

  toSnapshot(): WorldSnapshot {
    const sceneDiffs: Record<string, SceneDiff> = {};
    for (const [id, scene] of this.scenes) {
      sceneDiffs[id] = computeSceneDiff(scene.entities, scene.initialEntities);
    }
    for (const [id, diff] of this.pendingDiffs) {
      if (!(id in sceneDiffs)) sceneDiffs[id] = diff;
    }
    return {
      currentSceneId: this.currentSceneId,
      sceneDiffs,
      revealedSecrets: [...this.revealedSecrets],
      questProgress: Object.fromEntries(this.questProgress),
    };
  }

  toSave(): GameSave {
    return {
      version: CURRENT_SAVE_VERSION,
      world: this.toSnapshot(),
      player: {
        inventory: this.inventory,
        knowledge: this.knowledge.getProgress(),
      },
    };
  }
}

function mergeTerrainObjects(
  definition: SceneDefinition,
  terrainObjects: readonly ParsedTerrainObject[],
): SceneDefinition {
  const jsonById = new Map(definition.entities.map((e) => [e.id, e]));
  const terrainByName = new Map<string, ParsedTerrainObject>();
  for (const obj of terrainObjects) {
    if (obj.visible && obj.name && obj.type) terrainByName.set(obj.name, obj);
  }

  const merged: EntityDefinition[] = [];

  for (const obj of terrainByName.values()) {
    const json = jsonById.get(obj.name);
    const components = mergeTerrainComponents(buildComponentsFromProperties(obj.properties), json?.components);
    const def: EntityDefinition = {
      id: obj.name,
      definitionId: json?.definitionId ?? obj.type,
      position: { x: obj.x + obj.width / 2, y: obj.y + obj.height },
      ...(obj.rotation ? { rotation: obj.rotation } : {}),
      ...(json?.kind ? { kind: json.kind } : {}),
      ...(json?.asset ? { asset: json.asset } : {}),
      ...(json?.zIndex !== undefined ? { zIndex: json.zIndex } : {}),
      ...(json?.hidden !== undefined ? { hidden: json.hidden } : {}),
      ...(components ? { components } : {}),
    };
    merged.push(def);
  }

  for (const entity of definition.entities) {
    if (terrainByName.has(entity.id)) continue;
    merged.push(entity);
  }

  return { ...definition, entities: merged };
}

function mergeTerrainComponents(
  terrain: readonly EntityComponent[] | undefined,
  json: readonly EntityComponent[] | undefined,
): EntityComponent[] | undefined {
  if (!terrain && !json) return undefined;
  const components = new Map<ComponentType, EntityComponent>();
  for (const component of terrain ?? []) components.set(component.type, component);
  for (const component of json ?? []) {
    const existing = components.get(component.type);
    components.set(component.type, existing ? { ...existing, ...component } as EntityComponent : component);
  }
  return [...components.values()];
}

function buildComponentsFromProperties(
  props: Readonly<Record<string, unknown>>,
): EntityComponent[] | undefined {
  const keys = Object.keys(props);
  if (keys.length === 0) return undefined;
  const grouped = new Map<string, Record<string, unknown>>();
  for (const key of keys) {
    const sep = key.indexOf("_");
    if (sep === -1) continue;
    const type = key.slice(0, sep);
    const prop = key.slice(sep + 1);
    if (!grouped.has(type)) grouped.set(type, {});
    grouped.get(type)![prop] = props[key];
  }
  const portal = grouped.get("portal");
  if (portal && typeof portal["spawnX"] === "number" && typeof portal["spawnY"] === "number") {
    portal["spawnPoint"] = { x: portal["spawnX"], y: portal["spawnY"] };
    delete portal["spawnX"];
    delete portal["spawnY"];
  }
  if (grouped.size === 0) return undefined;
  const components: EntityComponent[] = [];
  for (const [type, data] of grouped) {
    components.push({ type: type as ComponentType, ...data } as EntityComponent);
  }
  return components;
}
